import { type Cancelable, type ThrottleOrDebounceOpts, useDebouncedFn } from 'beautiful-react-hooks'
import { type DependencyList, useEffect } from 'react'

/**
 * Same thing as 'beautiful-react-hooks' `useDebouncedFn` but flushes on unmount
 *
 * Solves: `Can't perform a React state update on an unmounted component`
 */
export function useLocalDebouncedFn<F extends Function>(
  fn: F,
  wait?: number,
  options?: Partial<ThrottleOrDebounceOpts>,
  dependencies?: DependencyList,
): F & Cancelable {
  const debouncedFunction = useDebouncedFn(fn, wait, options, dependencies)
  useEffect(() => {
    // Immediatly flush the debounced function on unmount
    // This is guaranteed to be there in prod but we mock it in tests without the flush method
    return () => debouncedFunction.flush?.()
  }, [debouncedFunction])
  return debouncedFunction
}
